import React from 'react'
import Head from 'next/head'
import { BreadcrumbJsonLd, LocalBusinessJsonLd, NextSeo } from 'next-seo'
import { useDispatch } from 'react-redux'
import OrlandoLocationHub from '../components/design-system/marketing/OrlandoLocationHub'
import QuotePop from '../components/DeferredQuotePopup'
import { BUSINESS_HOURS } from '../content/businessHours'
import * as gtag from '../lib/gtag'
import { SHOW_POPUP } from '../store/slices/ui'

const CANONICAL_URL = 'https://miamisoundrental.com/orlando'
const SEO_TITLE = 'Orlando AV Equipment Rental & AV Company | Miami Sound Rental'
const SEO_DESCRIPTION = 'Rent professional AV equipment from our Orlando branch or plan delivery, setup and managed production across Orlando, Tampa and Central Florida.'

const Orlando = () => {
  const dispatch = useDispatch()

  const openQuote = () => {
    gtag.event({ action: 'quote_click', label: 'leads', value: 3, category: 'orders' })
    dispatch(SHOW_POPUP({ key: 'quote', size: 'medium', body: <QuotePop /> }))
  }

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${CANONICAL_URL}#av-service`,
    name: 'Orlando AV Equipment Rental and Event Production',
    serviceType: 'Audiovisual equipment rental, delivery, setup and event production',
    url: CANONICAL_URL,
    description: SEO_DESCRIPTION,
    provider: { '@id': `${CANONICAL_URL}#location` },
    areaServed: [
      { '@type': 'City', name: 'Orlando' },
      { '@type': 'City', name: 'Tampa' },
      { '@type': 'AdministrativeArea', name: 'Central Florida' }
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Orlando audiovisual rental and production services',
      itemListElement: [
        'Speaker rental',
        'DJ equipment rental',
        'Karaoke rental',
        'Projector rental',
        'TV rental',
        'Modular stage rental',
        'LED video wall rental',
        'Audiovisual delivery and setup',
        'Managed event production'
      ].map(name => ({
        '@type': 'Offer',
        itemOffered: { '@type': 'Service', name }
      }))
    }
  }

  return (
    <>
      <NextSeo
        title={SEO_TITLE}
        description={SEO_DESCRIPTION}
        canonical={CANONICAL_URL}
        twitter={{
          handle: '@miamisoundrent1',
          site: '@miamisoundrent1',
          cardType: 'summary_large_image'
        }}
        openGraph={{
          type: 'website',
          url: CANONICAL_URL,
          title: SEO_TITLE,
          description: SEO_DESCRIPTION,
          images: [{
            url: 'https://miamisoundrental.com/Pictures/categories-v2/orlando-hero-hybrid-v2.jpg',
            width: 1600,
            height: 900,
            alt: 'Conference ballroom with a large LED video wall and classroom seating'
          }],
          site_name: 'Miami Sound Rental'
        }}
      />

      <BreadcrumbJsonLd itemListElements={[
        { position: 1, name: 'Home', item: 'https://miamisoundrental.com/' },
        { position: 2, name: 'Orlando', item: CANONICAL_URL }
      ]} />

      <LocalBusinessJsonLd
        type='Store'
        id={`${CANONICAL_URL}#location`}
        name='Miami Sound Rental — Orlando'
        description='Orlando audiovisual equipment rental, delivery, setup and managed event production.'
        url={CANONICAL_URL}
        telephone='+14078070052'
        address={{
          streetAddress: '8600 Commodity Cir #152',
          addressLocality: 'Orlando',
          addressRegion: 'FL',
          postalCode: '32819',
          addressCountry: 'US'
        }}
        geo={{ latitude: '28.445134', longitude: '-81.431191' }}
        images={[
          'https://miamisoundrental.com/Pictures/categories-v2/orlando-hero-hybrid-v2.jpg',
          'https://miamisoundrental.com/Pictures/orlando-partnerships.jpg'
        ]}
        openingHours={BUSINESS_HOURS}
        areaServed={[{
          geoMidpoint: { latitude: '28.445134', longitude: '-81.431191' },
          geoRadius: '160000'
        }]}
      />

      <Head>
        <script
          id='orlando-av-service-schema'
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
        />
      </Head>

      <OrlandoLocationHub onQuote={openQuote} />
    </>
  )
}

export default Orlando
